import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Building2, Users } from 'lucide-react';
import { companiesApi } from '@/features/brands/api';
import { useCurrentCompany } from '@/lib/branding/useCurrentBrand';
import { queryKeys } from '@/lib/api/queryKeys';
import { Card } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { Breadcrumbs } from '@/components/ui/Breadcrumbs';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { toast } from '@/components/ui/Toast';
import { useT } from '@/lib/i18n/useT';
import { HelpHint } from '@/features/help/HelpHint';
import { useFavoritable } from '@/features/favorites/FavoritableContext';
import { cn } from '@/lib/cn';
import type { CompanyType } from '@/lib/api/types';

/**
 * CH-12 — Settings › Workspace. El tipo de cuenta (marca o agencia) se elige
 * al darse de alta, vive en la raíz del workspace (`co_companies.companyType`,
 * la capa Onxite que leen todas las apps) y puede cambiar después sin perder
 * nada: una marca contrata a una agencia, una agencia se queda una marca en
 * casa. Agencia → marca sólo si no hay más de una marca colgando; el backend
 * lo rechaza con 409 si no.
 */
type Choice = Extract<CompanyType, 'Company' | 'Agency'>;

export function WorkspaceSettingsPage() {
  const t = useT();
  useFavoritable(t('settings.workspace.title'));
  const qc = useQueryClient();
  const { company, isLoading } = useCurrentCompany();
  const [pending, setPending] = useState<Choice | null>(null);

  const change = useMutation({
    mutationFn: (companyType: Choice) => companiesApi.update(company!.id, { companyType }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.companies.detail(company?.id ?? 0) });
      void qc.invalidateQueries({ queryKey: queryKeys.brands.all });
      toast({ tone: 'success', title: t('settings.workspace.savedToast') });
      setPending(null);
    },
    onError: (e: Error) => {
      toast({ tone: 'error', title: t('settings.workspace.errorToast'), description: e.message });
      setPending(null);
    },
  });

  const current = (company?.companyType as CompanyType | undefined) ?? undefined;
  const isRoot = company != null && company.parentId == null;
  const options: Array<{ id: Choice; icon: typeof Building2; title: string; body: string }> = [
    { id: 'Company', icon: Building2, title: t('settings.workspace.brand'), body: t('settings.workspace.brandBody') },
    { id: 'Agency', icon: Users, title: t('settings.workspace.agency'), body: t('settings.workspace.agencyBody') },
  ];

  return (
    <div className="page space-y-6">
      <Breadcrumbs items={[{ label: t('settings.title'), to: '/settings' }, { label: t('settings.workspace.title') }]} />
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-[var(--color-fg-primary)]">
          {t('settings.workspace.title')}
          <HelpHint section="settings-workspace" />
        </h1>
        <p className="mt-1 text-sm text-[var(--color-fg-muted)]">{t('settings.workspace.subtitle')}</p>
      </div>

      {isLoading || !company ? (
        <Skeleton className="h-40" />
      ) : (
        <Card className="space-y-4 p-5">
          <div>
            <p className="text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
              {t('settings.workspace.accountType')}
            </p>
            <p className="mt-1 text-sm text-[var(--color-fg-muted)]">
              {t('settings.workspace.currentFor', { name: company.name })}
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {options.map((o) => {
              const active = current === o.id || (o.id === 'Company' && current === 'Property');
              return (
                <button
                  key={o.id}
                  type="button"
                  disabled={!isRoot || change.isPending || active}
                  aria-pressed={active}
                  onClick={() => setPending(o.id)}
                  className={cn(
                    'flex items-start gap-3 rounded-xl border p-4 text-left transition-colors',
                    active
                      ? 'border-[var(--color-accent-ink)] bg-[var(--color-accent-06)]'
                      : 'border-[var(--color-border-default)] hover:border-[var(--color-accent-ink)]',
                    'disabled:cursor-not-allowed disabled:opacity-70',
                  )}
                >
                  <o.icon className="mt-0.5 h-5 w-5 shrink-0 text-[var(--color-accent-ink)]" />
                  <span>
                    <span className="block text-sm font-semibold text-[var(--color-fg-primary)]">
                      {o.title}
                      {active ? (
                        <span className="ml-2 rounded-full bg-[var(--color-accent-ink)] px-1.5 text-[10px] font-bold uppercase tracking-wide text-[var(--color-fg-on-accent)]">
                          {t('settings.workspace.current')}
                        </span>
                      ) : null}
                    </span>
                    <span className="mt-1 block text-xs text-[var(--color-fg-muted)]">{o.body}</span>
                  </span>
                </button>
              );
            })}
          </div>
          {!isRoot && <p className="text-xs text-[var(--color-fg-muted)]">{t('settings.workspace.notRoot')}</p>}
          <p className="text-xs text-[var(--color-fg-subtle)]">{t('settings.workspace.keepsData')}</p>
        </Card>
      )}

      <ConfirmDialog
        open={pending !== null}
        onOpenChange={(o) => !o && setPending(null)}
        title={t('settings.workspace.confirmTitle', {
          type: pending === 'Agency' ? t('settings.workspace.agency') : t('settings.workspace.brand'),
        })}
        description={t(pending === 'Agency' ? 'settings.workspace.confirmToAgency' : 'settings.workspace.confirmToBrand')}
        confirmLabel={t('settings.workspace.confirm')}
        loading={change.isPending}
        onConfirm={() => pending && change.mutate(pending)}
      />
    </div>
  );
}
